import { useMemo } from 'react';

import { appendRequestId, sanitizeRequestId } from '../utils/errors';
import { isTerminalApiAuthCode } from '../utils/authRecovery';
import { cloudflare502Diagnostic, type Cloudflare502Diagnostic } from './cloudflare502Diagnostic';
import { safeGitlabRequestId } from './gitlabRequestId';

/** HTTP failures retain their status so capability-aware callers can tell a
 * revoked resource grant from a transient service failure. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly requestId?: string,
    readonly contentType?: string,
    readonly cloudflare?: Cloudflare502Diagnostic
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type ResponseError = {
  message: string;
  code?: string;
  requestId?: string;
};

const GITLAB_UPDATES_TOKEN_PATH = '/api/conversations/gitlab/ws-token';

// true reports a terminal rejection; it must NOT trigger an interactive login.
export type UnauthorizedRecovery = (
  terminalRejection: boolean, failure: ApiError, signal?: AbortSignal | null, retryToken?: string
) => Promise<string | null>;

async function responseErrorMessage(
  response: Response,
  method: string,
  path: string
): Promise<ResponseError> {
  const text = await response.text();
  let message = text || response.statusText || `Request failed (${response.status})`;
  let code: string | undefined;
  let requestId = sanitizeRequestId(response.headers.get('x-request-id'));
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/problem+json')) {
    try {
      const problem = text ? (JSON.parse(text) as Record<string, unknown>) : {};
      const title = typeof problem.title === 'string' ? problem.title : '';
      const detail = typeof problem.detail === 'string' ? problem.detail : '';
      code = typeof problem.code === 'string' && problem.code ? problem.code : undefined;
      requestId = sanitizeRequestId(problem.request_id) ?? requestId;
      message = detail || title || message;
      if (code) {
        message = `${message} [${code}]`;
      }
    } catch {
      console.warn('Failed to parse problem-json response');
    }
  }
  return {
    message: `${method} ${path}: ${appendRequestId(message, requestId)}`,
    code,
    requestId
  };
}

async function apiRequest<T>(path: string, options: RequestInit, token: string): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  headers.set('Content-Type', 'application/json');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await fetch(path, {
    ...options,
    headers
  });
  if (!response.ok) {
    const method = options.method ?? 'GET';
    if (path === GITLAB_UPDATES_TOKEN_PATH) {
      const contentType = response.headers.get('content-type') ?? '';
      const cloudflare = response.status === 502 && /^text\/html(?:\s*;|\s*$)/i.test(contentType)
        ? cloudflare502Diagnostic(await response.text().catch(() => ''), globalThis.location?.hostname,
          response.headers.get('cf-ray'))
        : undefined;
      // Other token endpoint bodies may contain credentials, including JSON.
      throw new ApiError('GitLab update token request failed', response.status, undefined,
        safeGitlabRequestId(response.headers.get('x-request-id')),
        contentType, cloudflare);
    }
    const failure = await responseErrorMessage(response, method, path);
    throw new ApiError(failure.message, response.status, failure.code, failure.requestId);
  }
  const bodyText = await response.text();
  if (!bodyText) {
    return null as T;
  }
  try {
    return JSON.parse(bodyText) as T;
  } catch (err) {
    if (path === GITLAB_UPDATES_TOKEN_PATH) {
      throw new ApiError(`Invalid JSON response from ${path}`, response.status, undefined,
        safeGitlabRequestId(response.headers.get('x-request-id')),
        response.headers.get('content-type') ?? '');
    }
    throw new Error(`Invalid JSON response from ${path}`);
  }
}

async function recoverAndRetry<T>(
  request: (token: string) => Promise<T>,
  token: string,
  recoverUnauthorized?: UnauthorizedRecovery,
  signal?: AbortSignal | null
): Promise<T> {
  try {
    signal?.throwIfAborted();
    const value = await request(token);
    signal?.throwIfAborted();
    return value;
  } catch (error) {
    signal?.throwIfAborted();
    if (!(error instanceof ApiError) || error.status !== 401 || !token || !recoverUnauthorized) {
      throw error;
    }

    if (isTerminalApiAuthCode(error.code)) {
      await recoverUnauthorized(true, error, signal);
      signal?.throwIfAborted();
      throw error;
    }
    const refreshedToken = await recoverUnauthorized(false, error, signal);
    signal?.throwIfAborted();
    if (!refreshedToken) {
      throw error;
    }
    try {
      const value = await request(refreshedToken);
      signal?.throwIfAborted();
      return value;
    } catch (retryError) {
      signal?.throwIfAborted();
      if (retryError instanceof ApiError && retryError.status === 401) {
        // Refresh succeeded but API access did not. Stop here, including across
        // navigation; repeated login cannot fix a configuration mismatch.
        await recoverUnauthorized(true, retryError, signal, refreshedToken);
      }
      throw retryError;
    }
  }
}

export async function api<T>(
  path: string,
  options: RequestInit,
  token: string,
  recoverUnauthorized?: UnauthorizedRecovery
): Promise<T> {
  return recoverAndRetry(
    (requestToken) => apiRequest<T>(path, options, requestToken),
    token,
    recoverUnauthorized,
    options.signal
  );
}

async function apiBlobRequest(path: string, token: string): Promise<Blob> {
  const headers = new Headers();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await fetch(path, { headers });
  if (!response.ok) {
    const failure = await responseErrorMessage(response, 'GET', path);
    throw new ApiError(failure.message, response.status, failure.code, failure.requestId);
  }
  return response.blob();
}

export async function apiBlob(
  path: string,
  token: string,
  recoverUnauthorized?: UnauthorizedRecovery
): Promise<Blob> {
  return recoverAndRetry(
    (requestToken) => apiBlobRequest(path, requestToken),
    token,
    recoverUnauthorized
  );
}

async function apiUploadRequest<T>(path: string, file: Blob, token: string): Promise<T> {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await fetch(path, {
    method: 'POST',
    headers,
    body: file
  });
  if (!response.ok) {
    const failure = await responseErrorMessage(response, 'POST', path);
    throw new ApiError(failure.message, response.status, failure.code, failure.requestId);
  }
  const bodyText = await response.text();
  if (!bodyText) return null as T;
  try {
    return JSON.parse(bodyText) as T;
  } catch {
    throw new Error(`Invalid JSON response from ${path}`);
  }
}

/** Upload a replayable Blob without JSON/base64 expansion. */
export async function apiUpload<T>(
  path: string,
  file: Blob,
  token: string,
  recoverUnauthorized?: UnauthorizedRecovery
): Promise<T> {
  return recoverAndRetry(
    (requestToken) => apiUploadRequest<T>(path, file, requestToken),
    token,
    recoverUnauthorized
  );
}

export type ApiClient = ReturnType<typeof useApi>;

export function useApi(token: string, recoverUnauthorized?: UnauthorizedRecovery) {
  return useMemo(() => {
    return {
      get: <T,>(path: string, options: Pick<RequestInit, 'signal'> = {}) =>
        api<T>(path, options, token, recoverUnauthorized),
      post: <T,>(path: string, body?: unknown) =>
        api<T>(
          path,
          {
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined
          },
          token,
          recoverUnauthorized
        ),
      put: <T,>(path: string, body?: unknown) =>
        api<T>(
          path,
          {
            method: 'PUT',
            body: body ? JSON.stringify(body) : undefined
          },
          token,
          recoverUnauthorized
        ),
      patch: <T,>(path: string, body?: unknown) =>
        api<T>(
          path,
          {
            method: 'PATCH',
            body: body ? JSON.stringify(body) : undefined
          },
          token,
          recoverUnauthorized
        ),
      del: <T,>(path: string) =>
        api<T>(
          path,
          {
            method: 'DELETE'
          },
          token,
          recoverUnauthorized
        ),
      blob: (path: string) => apiBlob(path, token, recoverUnauthorized),
      upload: <T,>(path: string, file: Blob) =>
        apiUpload<T>(path, file, token, recoverUnauthorized)
    };
  }, [recoverUnauthorized, token]);
}
